import React, { useState, useEffect } from 'react';
import { Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Typography, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import InvItemAutocomplete from '../../components/InvItemAutocomplete';
import { createOpeningStock, getStores } from '../../api/stockLedger';
import useActiveStore from '../../hooks/useActiveStore';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { storeDisplayName } from '../../utils/storeEntity';
import { IPD_COLORS } from '../../theme';

// One blank line row. Opening stock is create-only, so there is no server-id / dirty tracking.
const LINE_DEFAULT = { item_id: '', quantity: '', unit_value: '', batch_number: '', expiry_date: '' };

// Total Cost is display-only — the backend recomputes it from quantity × unit_value.
function lineTotal(line) {
  const qty = Number(line.quantity);
  const val = Number(line.unit_value);
  if (!(qty > 0) || !(val >= 0)) return 0;
  return qty * val;
}

export default function OpeningStockForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  // Base path for the /stock section — robust regardless of the exact current URL.
  const stockBase = `${url.split('/stock/')[0]}/stock`;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stores, setStores] = useState([]);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);

  const { storeLocked, activeStoreId, assignedStoreIds } = useActiveStore();

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      store_id: '',
      opening_date: dayjs().format('YYYY-MM-DD'),
      remarks: '',
    },
  });

  const storeId = watch('store_id');

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores()
      .then(d => setStores(Array.isArray(d) ? d : (d?.data || d?.items || [])))
      .catch(() => {});
  }, []);

  // Store-scoped users work in one store only — pre-fill and lock it. Guarded on
  // membership: setting a value that is not in `options` renders the Select blank.
  const lockStore = storeLocked && stores.some(s => (s.store_id || s.id) === activeStoreId);

  useEffect(() => {
    if (lockStore) setValue('store_id', activeStoreId);
  }, [lockStore, activeStoreId, setValue]);


  function addLine(afterIndex) {
    setLines(l => {
      const newLine = { ...LINE_DEFAULT };
      if (afterIndex === undefined) return [...l, newLine];
      const next = [...l];
      next.splice(afterIndex + 1, 0, newLine);
      return next;
    });
  }
  function removeLine(i) { setLines(l => l.filter((_, idx) => idx !== i)); }
  function updateLine(i, key, val) {
    setLines(l => l.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  }

  const storeOptions = filterStoresByAssignment(stores, assignedStoreIds).map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) }));

  const grandTotal = lines.reduce((sum, line) => sum + lineTotal(line), 0);

  // A row with no item is an untouched blank — skipped. A row with an item but no
  // valid quantity is a mistake — block the save so nothing is silently dropped.
  function invalidLineMessage(line) {
    if (!line.item_id) return null;
    if (!(Number(line.quantity) > 0)) {
      return 'One of the item rows has an item selected but no valid quantity. Fix the quantity or remove the row before saving.';
    }
    return null;
  }

  async function save(data) {
    setError(null);
    for (const line of lines) {
      const msg = invalidLineMessage(line);
      if (msg) { setError(msg); return; }
    }
    const validLines = lines.filter(l => l.item_id && Number(l.quantity) > 0);
    if (validLines.length === 0) {
      setError('Add at least one item with a valid quantity.');
      return;
    }
    setSaving(true);
    try {
      // API: POST /inventory/stock/opening-stock/
      const saved = await createOpeningStock({
        store_id: data.store_id,
        opening_date: data.opening_date,
        remarks: data.remarks || undefined,
        lines: validLines.map(l => ({
          item_id: l.item_id,
          quantity: Number(l.quantity),
          unit_value: l.unit_value !== '' && l.unit_value != null ? Number(l.unit_value) : 0,
          batch_number: l.batch_number || undefined,
          expiry_date: l.expiry_date || undefined,
        })),
      });
      const newId = saved?.id || saved?.data?.id;
      if (newId) history.push(`${stockBase}/opening-stocks/${newId}`);
      else history.push(`${stockBase}/opening-stocks`);
    } catch (e) {
      console.error('[OpeningStockForm] save failed:', e);
      setError(e.message || 'Failed to save the opening stock.');
    } finally { setSaving(false); }
  }

  const onSubmit = handleSubmit(save);

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      <PageHeader
        eyebrow="Inventory Management"
        title="New Opening Stock"
        description="Record the one-time starting balance for items in a store. This can be done only once per item per store."
        actions={
          <>
            <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}>Cancel</InvButton>
            <InvButton type="button" onClick={onSubmit} loading={saving}>Save Opening Stock</InvButton>
          </>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SectionCard title="Opening Stock Details">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller name="store_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvSelect label="Store *" value={field.value} onChange={field.onChange}
                      options={storeOptions} error={errors.store_id} disabled={lockStore} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller name="opening_date" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvTextField {...field} label="Date *" type="date"
                      InputLabelProps={{ shrink: true }} error={errors.opening_date} />
                  )} />
              </Grid>
              <Grid item xs={12}>
                <Controller name="remarks" control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />} />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Items" sx={{ mt: 2.5 }}
            actions={<InvButton size="small" startIcon={<AddIcon />} onClick={addLine}>Add Row</InvButton>}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }} />
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 240 }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Qty *</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Value / Unit</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 140 }}>Batch</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 160 }}>Expiry</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Total Cost</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeLine(i)} sx={{ color: IPD_COLORS.error.text }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <InvItemAutocomplete
                          value={line.item_id}
                          storeId={storeId}
                          onChange={(itemId) => updateLine(i, 'item_id', itemId)}
                        />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.quantity} label=""
                          onChange={e => updateLine(i, 'quantity', e.target.value)}
                          type="number" inputProps={{ min: 0.01, step: 0.01 }} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.unit_value} label=""
                          onChange={e => updateLine(i, 'unit_value', e.target.value)}
                          type="number" inputProps={{ min: 0, step: 0.01 }} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.batch_number} label=""
                          onChange={e => updateLine(i, 'batch_number', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.expiry_date} label="" type="date"
                          InputLabelProps={{ shrink: true }}
                          onChange={e => updateLine(i, 'expiry_date', e.target.value)} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 13 }}>
                          {lineTotal(line).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => addLine(i)} sx={{ color: IPD_COLORS.primary }}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {lines.length === 0 ? (
              <Typography sx={{ color: IPD_COLORS.textSecondary, textAlign: 'center', py: 3 }}>
                Add at least one item.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1.5 }}>
                <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, mr: 1 }}>Grand Total:</Typography>
                <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14, fontWeight: 700 }}>
                  {grandTotal.toFixed(2)}
                </Typography>
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
